import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { API_URL } from '../../../api/api';
const API_BRANCH = `${API_URL}/branches`;
const API_EMPLOYEE = `${API_URL}/employees`;
const API_MEMBER = `${API_URL}/members`;
const API_GROUP = `${API_URL}/groups`;

const AddGroupLayer = () => {
    const { id } = useParams(); // id presence = edit mode
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const headers = { headers: { Authorization: `Bearer ${token}` } };


    const [members, setMembers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [employee, setEmployee] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [form, setForm] = useState({
        advisor_id: '',
        group_leader_id: '',
        branch_id: '',
        collectiontime: '',
        enrollmentdate: '',
        collectionday: '',
        groupname: '',
        email: '',
        mobile: '',
        altmobile: '',
        address: '',
        landmark: '',
        city: '',
        state: '',
        pincode: '',
        houseno: '',
        lat: '0',
        longs: '0',

        addressp: '',
        landmarkp: '',
        cityp: '',
        statep: '',
        pincodep: '',
        housenop: '',
        latp: '0',
        longsp: '0',

        status: 'active',
        sameAsCorrespondence: false,
    });

    // Convert members to react-select format
    const memberOptions = members.map(member => ({
        value: member.id,
        label: `${member.firstname} ${member.lastname}-${member.mobile}`
    }));

    // Find the currently selected member (by ID)
    const selectedMember = memberOptions.find(
        option => option.value === form.group_leader_id
    ) || null;

    // Format branches for react-select
    const branchOptions = branches.map(branch => ({
        value: branch.id,
        label: branch.branchname
    }));

    // Match current form.branch_id to an option
    const selectedBranch = branchOptions.find(
        option => option.value === form.branch_id
    ) || null;

    // Fetch branches, employees, members and group data (if editing)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const headers = { headers: { Authorization: `Bearer ${token}` } };
                const [branchRes, empRes, memberRes] = await Promise.all([
                    axios.get(API_BRANCH, headers),
                    axios.get(API_EMPLOYEE, headers),
                    axios.get(API_MEMBER, headers)
                ]);
                console.log(memberRes.data);
                setBranches(branchRes.data);
                setEmployee(empRes.data.data || empRes.data);
                setMembers(memberRes.data);
                if (id) {
                    // Fetch existing group data to edit
                    const { data } = await axios.get(`${API_GROUP}/${id}`, headers);
                    setForm({
                        ...data,
                        sameAsCorrespondence: false, // reset; user can toggle
                        // Convert null lat/long to empty string to avoid uncontrolled inputs
                        lat: data.lat || '',
                        longs: data.longs || '',
                        latp: data.latp || '',
                        longsp: data.longsp || '',
                    });
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
                Swal.fire("Unauthorized", "Please login again.", "error");
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token, navigate, id]);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;

        if (name === 'sameAsCorrespondence') {
            setForm(prev => {
                if (checked) {
                    return {
                        ...prev,
                        sameAsCorrespondence: true,
                        addressp: prev.address,
                        landmarkp: prev.landmark,
                        cityp: prev.city,
                        statep: prev.state,
                        pincodep: prev.pincode,
                        housenop: prev.houseno,
                        latp: prev.lat,
                        longsp: prev.longs,
                    };
                } else {
                    return {
                        ...prev,
                        sameAsCorrespondence: false,
                        addressp: '',
                        landmarkp: '',
                        cityp: '',
                        statep: '',
                        pincodep: '',
                        housenop: '',
                        latp: '',
                        longsp: '',
                    };
                }
            });
            return;
        }

        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const payload = {
                ...form,
                lat: form.lat ? parseFloat(form.lat) : null,
                longs: form.longs ? parseFloat(form.longs) : null,
                latp: form.latp ? parseFloat(form.latp) : null,
                longsp: form.longsp ? parseFloat(form.longsp) : null,
            };
            delete payload.sameAsCorrespondence;

            if (id) {
                // Update existing group
                await axios.put(`${API_GROUP}/${id}`, payload, { headers });
                Swal.fire("Success", "Group updated successfully!", "success");
            } else {
                // Add new group
                await axios.post(API_GROUP, payload, { headers });
                Swal.fire("Success", "Group added successfully!", "success");
            }

            navigate('/manage-groups');
        } catch (error) {
            console.error("Failed to submit form:", error);
            if (error.response?.data?.errors) {
                const messages = Object.values(error.response.data.errors)
                    .flat()
                    .join('<br>');
                Swal.fire({
                    icon: 'error',
                    title: 'Validation Errors',
                    html: messages,
                });
            } else {
                Swal.fire("Error", error.response?.data?.message || "Failed to submit form", "error");
            }
        }
    };

    if (loading) {
        return <p>Loading data...</p>;
    }

    return (
        <div className="container mt-4">
            <div className="card shadow">
                <div className="card-header bg-info text-white">
                    <p className="mb-0">{id ? "Update Group" : "Add New Group"}</p>
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        {/* Section: Advisor & Collection */}
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <label className="form-label">Associate/Advisor/Staff</label>
                                <select
                                    className="form-select"
                                    name="advisor_id"
                                    value={form.advisor_id}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Advisor</option>
                                    {employee.map(emp => (
                                        <option key={emp.id} value={emp.id}>{emp.firstname}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Group Leader</label>
                                <Select
                                    name="group_leader_id"
                                    options={memberOptions}
                                    value={selectedMember}
                                    onChange={(selected) =>
                                        setForm(prev => ({ ...prev, group_leader_id: selected ? selected.value : '' }))
                                    }
                                    isClearable
                                    placeholder="Select Member"
                                />
                            </div>


                            <div className="col-md-6 mt-3">
                                <label className="form-label">Collection Time</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    name="collectiontime"
                                    value={form.collectiontime}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-md-6 mt-3">
                                <label className="form-label">Enrollment Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="enrollmentdate"
                                    value={form.enrollmentdate}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Section: Branch Info */}
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <label className="form-label">Branch</label>
                                <Select
                                    name="branch_id"
                                    options={branchOptions}
                                    value={selectedBranch}
                                    onChange={(selected) =>
                                        setForm(prev => ({ ...prev, branch_id: selected ? selected.value : '' }))
                                    }
                                    isClearable
                                    placeholder="Select Branch"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />

                            </div>

                            <div className="col-md-6 mt-3">
                                <label className="form-label">Collection Day</label>
                                <select
                                    className="form-select"
                                    name="collectionday"
                                    value={form.collectionday}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Day</option>
                                    <option>Monday</option>
                                    <option>Tuesday</option>
                                    <option>Wednesday</option>
                                    <option>Thursday</option>
                                    <option>Friday</option>
                                    <option>Saturday</option>
                                    <option>Sunday</option>
                                </select>
                            </div>
                        </div>

                        {/* Section: Group Info */}
                        <div className="card mb-4">
                            <div className="card-header bg-light">
                                <strong>Group's Info</strong>
                            </div>
                            <div className="card-body row">
                                <div className="col-md-6">
                                    <label className="form-label">Group Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="groupname"
                                        value={form.groupname}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label className="form-label mt-3">Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Mobile</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="mobile"
                                        value={form.mobile}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label className="form-label mt-3">Alt Mobile</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="altmobile"
                                        value={form.altmobile}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section: Correspondence Address */}
                        <div className="card mb-4">
                            <div className="card-header bg-light">
                                <strong>Correspondence Address</strong>
                            </div>
                            <div className="card-body row">
                                <div className="col-md-6">
                                    <label className="form-label">Address</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="address"
                                        value={form.address}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label className="form-label mt-3">Landmark</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="landmark"
                                        value={form.landmark}
                                        onChange={handleChange}
                                    />
                                    <label className="form-label mt-3">City</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label className="form-label mt-3">State</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="state"
                                        value={form.state}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Pincode</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="pincode"
                                        value={form.pincode}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label className="form-label mt-3">House No.</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="houseno"
                                        value={form.houseno}
                                        onChange={handleChange}
                                    />
                                    <label className="form-label mt-3">Lat</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="lat"
                                        value={form.lat}
                                        onChange={handleChange}
                                    />
                                    <label className="form-label mt-3">Long</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="longs"
                                        value={form.longs}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section: Permanent Address */}
                        <div className="card mb-4">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <strong>Permanent Address</strong>
                                <label className="form-check-label">
                                    <input
                                        type="checkbox"
                                        className="form-check-input me-2"
                                        name="sameAsCorrespondence"
                                        checked={form.sameAsCorrespondence}
                                        onChange={handleChange}
                                    />
                                    Same as Correspondence Address
                                </label>
                            </div>
                            <div className="card-body row">
                                <div className="col-md-6">
                                    <label className="form-label">Address</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="addressp"
                                        value={form.addressp}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                    <label className="form-label mt-3">Landmark</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="landmarkp"
                                        value={form.landmarkp}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                    <label className="form-label mt-3">City</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="cityp"
                                        value={form.cityp}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                    <label className="form-label mt-3">State</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="statep"
                                        value={form.statep}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Pincode</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="pincodep"
                                        value={form.pincodep}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                    <label className="form-label mt-3">House No.</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="housenop"
                                        value={form.housenop}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                    <label className="form-label mt-3">Lat</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="latp"
                                        value={form.latp}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                    <label className="form-label mt-3">Long</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="longsp"
                                        value={form.longsp}
                                        onChange={handleChange}
                                        disabled={form.sameAsCorrespondence}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="text-end">
                            <button type="submit" className="btn btn-success me-2">
                                {id ? "Update Group" : "Add Group"}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddGroupLayer;
